import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/lib/ContentContext";
import { submitLead } from "@/lib/api";
import { MaskedLine } from "@/components/Reveal";

const plans = ["Self-Serve (8%)", "Managed (12%)", "Enterprise (Custom)", "Not sure yet"];

export default function BookDemo() {
  const { budgetBands } = useContent();
  const [form, setForm] = useState({ name: "", work_email: "", company: "", role: "", monthly_budget: "", plan_interest: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email)) errs.work_email = "Please use a valid work email.";
    if (!form.company.trim()) errs.company = "Company is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const lead = await submitLead(form);
      setDone(lead);
      window.gtag?.("event", "generate_lead", { form: "book_demo" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Submission failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen" data-testid="book-demo-page">
      <div className="container-riq pt-16 pb-24 lg:pt-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">Book a Demo</p>
          <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl">
            <MaskedLine delay={0.1}>See the platform.</MaskedLine>
            <MaskedLine delay={0.22}>
              Keep the <span className="accent-word">receipts.</span>
            </MaskedLine>
          </h1>
          <p className="mt-6 text-[#4A5A63] leading-relaxed">
            A 30-minute session with a trading specialist: live platform walkthrough, fee-model breakdown for your spend level, and a log-level data sample from a real auction.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              [ShieldCheck, "No obligation, no cold-call ambush"],
              [Lock, "Your details go to our sales team — nowhere else"],
              [FileText, "Walk away with a written fee comparison for your spend"],
            ].map(([Icon, t]) => (
              <li key={t} className="flex items-center gap-3 text-sm font-medium">
                <span className="w-9 h-9 border border-[#D8E2E6] bg-[#E4F3F7] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#0E7A8F]" aria-hidden="true" />
                </span>
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-10 card-brut-sm p-5">
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#4A5A63]">Average response time</p>
            <p className="font-display font-black text-3xl mt-1">Under 4 hours</p>
            <p className="text-xs text-[#4A5A63] mt-1">During business hours, US / EU / APAC coverage.</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="card-brut p-8 sm:p-12 text-center" data-testid="demo-confirmation">
              <span className="inline-flex w-16 h-16 border border-[#D8E2E6] bg-[#E4F3F7] items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#0E7A8F]" aria-hidden="true" />
              </span>
              <h2 className="font-display font-black text-3xl tracking-tight mt-6">Request received.</h2>
              <p className="mt-3 text-[#4A5A63] max-w-md mx-auto leading-relaxed">
                Thanks, {done.name.split(" ")[0]}. Your demo request is with our sales team — expect a reply at <strong>{done.work_email}</strong> within 4 business hours.
              </p>
              <p className="mt-4 font-mono2 text-[10px] uppercase tracking-widest text-[#4A5A63]">
                Reference: {done.id.slice(0, 8)} · CRM sync: {done.integrations?.[0]?.status === "mocked" ? "pending (integration keys not yet configured)" : "done"}
              </p>
              <Link to="/why-responsiq" className="btn-outline mt-8 inline-flex" data-testid="confirmation-continue-btn">
                Meanwhile: us vs. the black box
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="card-brut p-6 sm:p-10" noValidate data-testid="demo-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="label-riq" htmlFor="f-name">Full name *</label>
                  <input id="f-name" className="input-riq" value={form.name} onChange={set("name")} autoComplete="name" data-testid="form-name-input" />
                  {errors.name && <p className="text-xs text-[#0E7A8F] mt-1 font-medium" role="alert">{errors.name}</p>}
                </div>
                <div>
                  <label className="label-riq" htmlFor="f-email">Work email *</label>
                  <input id="f-email" type="email" className="input-riq" value={form.work_email} onChange={set("work_email")} autoComplete="email" data-testid="form-email-input" />
                  {errors.work_email && <p className="text-xs text-[#0E7A8F] mt-1 font-medium" role="alert">{errors.work_email}</p>}
                </div>
                <div>
                  <label className="label-riq" htmlFor="f-company">Company *</label>
                  <input id="f-company" className="input-riq" value={form.company} onChange={set("company")} autoComplete="organization" data-testid="form-company-input" />
                  {errors.company && <p className="text-xs text-[#0E7A8F] mt-1 font-medium" role="alert">{errors.company}</p>}
                </div>
                <div>
                  <label className="label-riq" htmlFor="f-role">Role</label>
                  <input id="f-role" className="input-riq" placeholder="e.g. Head of Programmatic" value={form.role} onChange={set("role")} autoComplete="organization-title" data-testid="form-role-input" />
                </div>
                <div>
                  <label className="label-riq" htmlFor="f-budget">Monthly media budget</label>
                  <select id="f-budget" className="input-riq" value={form.monthly_budget} onChange={set("monthly_budget")} data-testid="form-budget-select">
                    <option value="">Select a range</option>
                    {budgetBands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-riq" htmlFor="f-plan">Plan interest</label>
                  <select id="f-plan" className="input-riq" value={form.plan_interest} onChange={set("plan_interest")} data-testid="form-plan-select">
                    <option value="">Select a plan</option>
                    {plans.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="label-riq" htmlFor="f-message">What should we prepare?</label>
                  <textarea id="f-message" rows={4} className="input-riq resize-none" placeholder="Channels, KPIs, current DSP pain points…" value={form.message} onChange={set("message")} data-testid="form-message-input" />
                </div>
              </div>
              <button type="submit" className="btn-primary w-full mt-7 disabled:opacity-60" disabled={busy} data-testid="demo-submit-btn">
                {busy ? "Sending…" : "Request my demo"}
              </button>
              <p className="mt-4 text-xs text-[#4A5A63] leading-relaxed">
                By submitting you agree to our <Link to="/privacy" className="underline hover:text-[#0E7A8F]">Privacy Policy</Link>. We never sell or share your details.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
